import { Image, Skeleton } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { ErrorMessage } from 'components/ErrorMessage';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  Crop,
  makeAspectCrop,
  PixelCrop,
  ReactCropProps,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import createCroppedImage from './createCroppedImage';

export type CropVariant =
  | 'circle'
  | 'square'
  | 'portrait'
  | 'landscape'
  | 'none';

interface CroppableImageProps {
  src: string;
  imageName: string;
  variant?: CropVariant;
  onImageUpdate(newImageFile: File): void;
}

export const CroppableImage = ({
  src,
  imageName,
  variant = 'none',
  onImageUpdate,
}: CroppableImageProps) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [initialCrop, _setInitialCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [hasError, setHasError] = useState(false);
  const [croppedFile, setCroppedFile] = useState<File>();
  const debugCropping = false; // Set this to true to debug the file cropping visually

  const variantProps = {
    circularCrop: false,
  } as Pick<ReactCropProps, 'circularCrop' | 'aspect'>;

  switch (variant) {
    case 'landscape':
      variantProps.aspect = 16 / 9;
      variantProps.circularCrop = false;
      break;
    case 'portrait':
      variantProps.aspect = 9 / 16;
      variantProps.circularCrop = false;
      break;
    case 'square':
      variantProps.aspect = 1;
      variantProps.circularCrop = false;
      break;
    case 'circle':
      variantProps.aspect = 1;
      variantProps.circularCrop = true;
      break;
    default: // case 'none':
      variantProps.circularCrop = false;
  }

  const setInitialCrop = useCallback((width: number, height: number) => {
    const partialCrop: Partial<Crop> = {
      unit: '%',
      width: 75,
      height: 75,
    };
    const aspect = variantProps.aspect;
    const defaultCrop = aspect
      ? makeAspectCrop(partialCrop, aspect, width, height)
      : ({ unit: '%', width: 100, height: 100, x: 0, y: 0 } as Crop);
    const defaultCropCentered = centerCrop(defaultCrop, width, height);

    _setInitialCrop(defaultCropCentered);
    setCompletedCrop(convertToPixelCrop(defaultCropCentered, width, height));
  }, []);

  const generateImageFromCrop = useCallback(
    (name: string, image: HTMLImageElement, crop: PixelCrop) => {
      createCroppedImage(image, { crop })
        .then((blob) => {
          const image = new File([blob], name, { type: 'image/png' });
          if (debugCropping) {
            setCroppedFile(image);
          }
          console.log('onImageUpdate...');
          onImageUpdate(image);
        })
        .catch((error) => {});
    },
    [onImageUpdate, setCroppedFile],
  );

  useEffect(() => {
    if (
      imageLoaded &&
      completedCrop?.width &&
      completedCrop?.height &&
      imgRef.current
    ) {
      generateImageFromCrop(imageName, imgRef.current, completedCrop);
    }
  }, [
    imageName,
    completedCrop,
    imgRef.current,
    imageLoaded,
    generateImageFromCrop,
  ]);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  if (hasError) {
    return (
      <ErrorMessage
        size="lg"
        title={t`Unable to load image`}
        description={t`Try a different image or try again later.`}
      />
    );
  }
  return (
    <>
      <Skeleton isLoaded={imageLoaded} minH={48}>
        <ReactCrop
          crop={initialCrop}
          onChange={(c) => _setInitialCrop(c)}
          onComplete={(c) => setCompletedCrop(c)}
          keepSelection
          ruleOfThirds
          {...variantProps}
        >
          <Image
            ref={imgRef}
            onLoad={(event) => {
              const { width, height } = event.nativeEvent
                .target as HTMLImageElement;
              setInitialCrop(width, height);
              setImageLoaded(true);
            }}
            onError={() => {
              setHasError(true);
            }}
            hidden={!imageLoaded}
            src={src}
            w="100%"
            alt={t`The image you have selected`}
            crossOrigin="anonymous"
          />
        </ReactCrop>
      </Skeleton>
      {debugCropping && (
        <div>
          <pre>Debug, crop preview: </pre>
          {croppedFile && (
            <img
              src={URL.createObjectURL(croppedFile)}
              style={{ border: '2px solid red', width: '50%' }}
            />
          )}
        </div>
      )}
    </>
  );
};
